import { find, prefersReducedMotion } from "./dom";

/**
 * The "back to top" button.
 *
 * <p>Built here rather than in the Razor layout for the same reason as the
 * rail arrows: without the bundle it would sit in the corner permanently,
 * with nothing to hide it and nothing to make it work.</p>
 *
 * <p>The masthead is sticky, so the menu is never out of reach — what this
 * solves is the return trip. /boende/ is 10 040 px on a 375-wide phone,
 * twelve screens, and a phone has no Home key.</p>
 */

// Two conditions, and both are needed. Distance alone would put the button on
// a page with one screen left to scroll, where it saves nothing; page length
// alone would show it before the reader has gone anywhere.
const SHOW_AFTER_SCREENS = 1.5;
const MIN_PAGE_SCREENS = 3;

const ARROW = `<svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true"
  stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
  <path d="M12 19V5"/><path d="M5 12l7-7 7 7"/></svg>`;

/** Adds the scroll-to-top button and keeps its visibility in step with the scroll position. */
export function initToTop(): void {
  const masthead = find<HTMLElement>(".masthead");

  const button = document.createElement("button");
  button.type = "button";
  button.className = "to-top";
  button.setAttribute("aria-label", "Till toppen av sidan");
  button.innerHTML = ARROW;
  document.body.append(button);

  let shown = false;
  // Page height and viewport are read once after the page or the window has
  // changed size, never per scroll event: reading scrollHeight forces layout,
  // and doing that on every scroll is the classic way to make a long page
  // stutter. The read waits for the first scroll after such a change rather
  // than happening at once. Measuring right after appending the button forced
  // a synchronous layout on every page load (PageSpeed, 2026-10-07), and
  // nothing needs the numbers before the reader scrolls.
  let worthIt = false;
  let limit = 0;
  let stale = true;

  const measure = (): void => {
    const screen = window.innerHeight;
    worthIt = document.documentElement.scrollHeight > screen * MIN_PAGE_SCREENS;
    limit = screen * SHOW_AFTER_SCREENS;
  };

  // One class, and CSS owns the rest. The button is hidden with visibility,
  // not with the hidden attribute: visibility keeps it out of the tab order
  // just as well, but it also transitions, so there is no frame to wait for
  // before the fade and no event to wait for after it. Hanging the hidden
  // state on transitionend left the button invisible but still focusable
  // whenever the transition did not run.
  const update = (): void => {
    if (stale) {
      measure();
      stale = false;
    }
    const wanted = worthIt && window.scrollY > limit;
    if (wanted !== shown) {
      shown = wanted;
      button.classList.toggle("to-top--in", wanted);
    }
  };

  button.addEventListener("click", () => {
    window.scrollTo({ top: 0, behavior: prefersReducedMotion() ? "auto" : "smooth" });

    // Scrolling moves the page, not the focus. Without this a keyboard user
    // lands back at the top visually and then tabs on from where they were,
    // several screens down. preventScroll keeps the jump from cutting the
    // smooth scroll short.
    const first = masthead === null ? null : find<HTMLElement>("a[href], button", masthead);
    first?.focus({ preventScroll: true });
  });

  window.addEventListener("scroll", update, { passive: true });
  // Lazy-loaded images and opened disclosures change the page height after
  // load; the observer only marks the numbers as stale, so it reads nothing.
  new ResizeObserver(() => {
    stale = true;
  }).observe(document.body);
  window.addEventListener("resize", () => {
    stale = true;
    update();
  }, { passive: true });
}
